import { useState, useEffect, useRef } from 'react'
import { mediaApi, type StickerPack, type StickerItem } from '../../api/media'
import { useUiStore } from '../../stores/uiStore'
import { Checkbox } from '../ui/Checkbox'

interface Props {
  onBack: () => void
}

type View = 'list' | 'create' | { pack: StickerPack & { items?: StickerItem[] } }

function PackDetail({
  pack: initialPack,
  onBack,
  onDelete,
}: {
  pack: StickerPack & { items?: StickerItem[] }
  onBack: () => void
  onDelete: () => void
}) {
  const [pack, setPack] = useState(initialPack)
  const [emoji, setEmoji] = useState('😀')
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const { addToast } = useUiStore()

  const handleAddSticker = async (file: File) => {
    setUploading(true)
    try {
      const uploaded = await mediaApi.upload(file)
      const item = await mediaApi.addStickerItem(pack.id, uploaded.id, emoji)
      setPack((p) => ({ ...p, items: [...(p.items ?? []), item] }))
      addToast('success', 'Стикер добавлен!')
    } catch {
      addToast('error', 'Не удалось добавить стикер')
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = async (itemId: string) => {
    setDeleting(itemId)
    try {
      await mediaApi.removeStickerItem(pack.id, itemId)
      setPack((p) => ({ ...p, items: (p.items ?? []).filter((i) => i.id !== itemId) }))
    } catch {
      addToast('error', 'Не удалось удалить стикер')
    } finally {
      setDeleting(null)
    }
  }

  const handleDeletePack = async () => {
    try {
      await mediaApi.deleteStickerPack(pack.id)
      onDelete()
    } catch {
      addToast('error', 'Не удалось удалить пак')
    }
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary text-sm">← Back</button>
        <p className="text-txt-primary font-semibold flex-1 truncate">{pack.name}</p>
        <button onClick={handleDeletePack} className="text-red-400 hover:text-red-300 text-xs">Delete</button>
      </div>

      {/* Add sticker */}
      <div className="flex gap-2 items-center">
        <input
          value={emoji}
          onChange={(e) => setEmoji(e.target.value)}
          maxLength={4}
          className="w-16 bg-bg-input text-txt-primary text-center rounded-xl px-2 py-2 text-lg outline-none focus:ring-1 focus:ring-accent"
          placeholder="😀"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex-1 bg-accent hover:bg-blue-500 text-white py-2 rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
        >
          {uploading ? 'Загрузка…' : '+ Добавить стикер'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAddSticker(f); e.target.value = '' }} />
      </div>

      {/* Stickers grid */}
      {(pack.items ?? []).length === 0 ? (
        <p className="text-txt-meta text-xs text-center py-6">Стикеров нет. Загрузите изображения выше.</p>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {(pack.items ?? []).map((item) => (
            <div key={item.id} className="relative group aspect-square bg-bg-input rounded-xl overflow-hidden">
              <img src={mediaApi.getThumbnailURL(item.file_id, '200x200')} alt={item.emoji} className="w-full h-full object-contain" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  onClick={() => handleRemove(item.id)}
                  disabled={deleting === item.id}
                  className="text-white text-xs disabled:opacity-40"
                >
                  {deleting === item.id ? '…' : '✕'}
                </button>
              </div>
              <span className="absolute bottom-0.5 right-0.5 text-xs">{item.emoji}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function CreateForm({ onBack, onCreate }: { onBack: () => void; onCreate: (p: StickerPack) => void }) {
  const [name, setName] = useState('')
  const [isPublic, setIsPublic] = useState(false)
  const [loading, setLoading] = useState(false)
  const { addToast } = useUiStore()

  const handleCreate = async () => {
    if (!name.trim()) return
    setLoading(true)
    try {
      const pack = await mediaApi.createStickerPack(name.trim(), '', isPublic)
      addToast('success', 'Пак создан!')
      onCreate(pack)
    } catch {
      addToast('error', 'Не удалось создать пак')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary text-sm">← Back</button>
        <p className="text-txt-primary font-semibold">New Sticker Pack</p>
      </div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Название пака"
        className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
      />
      <Checkbox checked={isPublic} onChange={setIsPublic} label="Сделать пак публичным" />
      <button
        onClick={handleCreate}
        disabled={loading || !name.trim()}
        className="w-full bg-accent hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
      >
        {loading ? 'Создание…' : 'Создать пак'}
      </button>
    </div>
  )
}

export function StickerPackCreator({ onBack }: Props) {
  const [packs, setPacks] = useState<StickerPack[]>([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<View>('list')
  const { addToast } = useUiStore()

  useEffect(() => {
    mediaApi.getStickerPacks()
      .then((r) => setPacks(r.packs ?? []))
      .catch(() => addToast('error', 'Не удалось загрузить паки'))
      .finally(() => setLoading(false))
  }, [addToast])

  const content = () => {
    if (view === 'create') {
      return (
        <CreateForm
          onBack={() => setView('list')}
          onCreate={(p) => { setPacks((prev) => [p, ...prev]); setView({ pack: p }) }}
        />
      )
    }

    if (typeof view === 'object') {
      return (
        <PackDetail
          pack={view.pack}
          onBack={() => setView('list')}
          onDelete={() => { setPacks((prev) => prev.filter((p) => p.id !== view.pack.id)); setView('list') }}
        />
      )
    }

    if (loading) return (
      <div className="flex justify-center py-8">
        <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    )

    return (
      <div className="p-4 space-y-3">
        <button
          onClick={() => setView('create')}
          className="w-full bg-accent hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
        >
          + Create New Pack
        </button>

        {packs.length === 0 ? (
          <p className="text-txt-meta text-sm text-center py-6">No custom packs yet</p>
        ) : (
          <div className="space-y-2">
            {packs.map((p) => (
              <button
                key={p.id}
                onClick={() => setView({ pack: p })}
                className="w-full text-left bg-bg-input rounded-2xl px-4 py-3 hover:bg-bg-hover transition-colors flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-bg-hover flex items-center justify-center text-xl shrink-0">
                  {p.thumbnail ? (
                    <img src={mediaApi.getThumbnailURL(p.thumbnail, '200x200')} alt={p.name} className="w-full h-full rounded-xl object-cover" />
                  ) : '🎨'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-txt-primary text-sm font-medium truncate">{p.name}</p>
                  <p className="text-txt-meta text-xs">{(p.items ?? []).length} stickers</p>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-txt-meta shrink-0">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">Мои стикер-паки</h2>
      </div>
      <div className="flex-1 overflow-y-auto">
        {content()}
      </div>
    </div>
  )
}
